import React from "react";
import {
  AppBar,
  Box,
  IconButton,
  Toolbar,
  Typography,
  Icon,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  alpha,
  useTheme,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { BranchSelector } from "../BranchSelector";
import { useBranch } from "../BranchContext";

const TRANSITION_DURATION = 0.2;
const HOVER_ANIMATION = {
  transition: `all ${TRANSITION_DURATION}s cubic-bezier(0.4, 0, 0.2, 1)`,
};

interface HeaderProps {
  drawerWidth: number;
  onDrawerToggle: () => void;
  onLogout: () => void;
  user: any;
  mobileOpen: boolean;
}

export const AccountingHeader: React.FC<HeaderProps> = ({
  drawerWidth,
  onDrawerToggle,
  onLogout,
  user,
  mobileOpen,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [userMenuAnchor, setUserMenuAnchor] =
    React.useState<null | HTMLElement>(null);

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const getBusinessName = () => {
    if (!user) return "Accounting Dashboard";
    return user?.business_name || "Accounting Dashboard";
  };

  const { selectedBranch, setSelectedBranch } = useBranch();

  const handleBranchChange = (branchId: string) => {
    setSelectedBranch(branchId);
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        maxHeight: "72px",
        width: { sm: `calc(100% - ${drawerWidth}px)` },
        ml: { sm: `${drawerWidth}px` },
        background: "transparent",
        backdropFilter: "blur(10px)",
        borderBottom: `1px solid ${alpha(
          theme.palette.divider,
          theme.palette.mode === "dark" ? 0.1 : 0.05
        )}`,
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background:
            theme.palette.mode === "dark"
              ? `linear-gradient(to right, ${alpha(
                  theme.palette.background.default,
                  0.95
                )}, ${alpha(theme.palette.background.default, 0.98)})`
              : `linear-gradient(to right, ${alpha(
                  theme.palette.background.paper,
                  0.9
                )}, ${alpha(theme.palette.background.paper, 0.98)})`,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
          zIndex: -1,
        },
      }}
    >
      <Toolbar
        sx={{
          height: { xs: 70, md: 80 },
          px: { xs: 2, md: 3, lg: 4 },
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {/* Left Section */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
            <IconButton
              onClick={onDrawerToggle}
              sx={{
                display: { sm: "none" },
                ...HOVER_ANIMATION,
                width: 40,
                height: 40,
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                },
                "& .menu-toggle": {
                  transition: "transform 0.3s ease",
                  transform: mobileOpen ? "rotate(180deg)" : "rotate(0deg)",
                },
              }}
            >
              <Icon className="menu-toggle">menu_open</Icon>
            </IconButton>

            <Box
              sx={{
                display: { xs: "none", sm: "flex" },
                alignItems: "center",
                gap: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  letterSpacing: "-0.3px",
                }}
              >
                {getBusinessName()}
              </Typography>
              <Divider
                orientation="vertical"
                flexItem
                sx={{ height: 24, my: "auto" }}
              />
            </Box>
          </Box>

          {/* Right Section */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* Branch Selector */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <BranchSelector
                selectedBranch={selectedBranch}
                onBranchChange={handleBranchChange}
                size="small"
                showLabel={true}
                variant="compact"
              />
            </Box>

            {/* User Menu */}
            <Tooltip title="Account">
              <IconButton
                onClick={handleUserMenuOpen}
                sx={{
                  ...HOVER_ANIMATION,
                  width: 40,
                  height: 40,
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    bgcolor: theme.palette.primary.main,
                  }}
                >
                  {user?.first_name?.charAt(0) || "U"}
                </Avatar>
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Toolbar>
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
            border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {user?.first_name} {user?.last_name}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", fontSize: "0.75rem" }}
          >
            {user?.email || "user@example.com"}
          </Typography>
        </Box>
        <Divider />
        <MenuItem
          onClick={() => {
            handleUserMenuClose();
            navigate("/accounting/settings");
          }}
          sx={{
            py: 1.5,
            px: 2,
            fontSize: "0.875rem",
            ...HOVER_ANIMATION,
          }}
        >
          <Icon sx={{ mr: 2, fontSize: 20 }}>settings</Icon>
          Settings
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleUserMenuClose();
            onLogout();
          }}
          sx={{
            py: 1.5,
            px: 2,
            fontSize: "0.875rem",
            color: "error.main",
            ...HOVER_ANIMATION,
          }}
        >
          <Icon sx={{ mr: 2, fontSize: 20 }}>logout</Icon>
          Sign Out
        </MenuItem>
      </Menu>
    </AppBar>
  );
};
